import { levelPercentages } from './rubricLevelPercentages';

export const ADVANCED_MARKING_DEFAULTS = {
  specificCommentQuantity: 'ALL',
  aiReplyDetail: 'FULL_REWRITE',
  sophisticationEnabled: false,
  rubricDifficulty: 'BALANCED',
  summaryLength: 'FULL',
};

export const stripNullValues = (obj) => {
  if (!obj || typeof obj !== 'object') return {};
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v != null));
};

export const FEEDBACK_AMOUNT_DEFAULT = 'EXTENSIVE';

export const SPECIFIC_COMMENT_OPTIONS = [
  {
    value: 'FEW',
    label: 'Essentials only',
    helper: 'Only the most critical comments on key issues.',
  },
  {
    value: 'BALANCED',
    label: 'Targeted',
    helper: 'Several specific comments but for key areas only.',
  },
  {
    value: 'ALL',
    label: 'Extensive',
    helper: 'Detailed comments across most of the response.',
  },
];

export const AI_REPLY_OPTIONS = [
  {
    value: 'NONE',
    label: 'Identify issues only',
    helper: 'Highlights issues without suggesting replacement wording.',
  },
  {
    value: 'SUGGESTION',
    label: 'Suggest ideas',
    helper: 'Provides brief ideas students can apply in their own words.',
  },
  {
    value: 'SLIGHTLY_IMPROVED',
    label: 'Give guided suggestions',
    helper:
      "Applies advice directly to the student's work by lightly adjusting part of their original to model improvement, without replacing their voice.",
  },
  {
    value: 'FULL_REWRITE',
    label: 'Show exemplars',
    helper: 'Shows fully modelled alternatives for comparison and learning.',
  },
];

export const RUBRIC_DIFFICULTY_OPTIONS = [
  {
    value: 'EASY',
    label: 'Supportive',
    helper: 'Prioritises confidence-building and positive momentum in grading.',
  },
  {
    value: 'BALANCED',
    label: 'Balanced',
    helper: 'Marking will align with standard grading distributions',
  },
  {
    value: 'VERY_HARD',
    label: 'Challenging',
    helper:
      'Uses the harshest possible standard and makes top-band results very hard to earn.',
  },
];

export const SUMMARY_LENGTH_OPTIONS = [
  {
    value: 'SHORT',
    label: 'Short (usually 3-4 sentences)',
    helper: '',
  },
  {
    value: 'FULL',
    label: 'Detailed (1 paragraph + strengths/targets)',
    helper: 'One short overview paragraph, then 3-4 strengths and 3-4 targets.',
  },
];

export const FEEDBACK_SUPPORT_OPTIONS = [
  {
    value: 'NEXT_LEVEL_IMPROVEMENT',
    label: 'Progress and next-level improvement',
    helper:
      'Focuses on helping students move one level beyond their current performance.',
  },
  {
    value: 'EXEMPLARY_PERFORMANCE',
    label: 'Exemplary performance',
    helper: 'Pushes students toward polished, top-band execution.',
  },
];

export const FEEDBACK_AMOUNT_OPTIONS = [
  {
    value: 'VERY_LIGHT',
    label: 'Essentials only',
    helper: 'Only the most critical in-text comments, with a brief summary.',
  },
  {
    value: 'BALANCED',
    label: 'Targeted',
    helper: 'Several in-text comments for key areas, with a moderate summary.',
  },
  {
    value: 'EXTENSIVE',
    label: 'Extensive',
    helper:
      'Detailed in-text comments across most of the response, with a fuller summary.',
  },
];

export const normalizeStudentFeedbackAmount = (value) => {
  switch (value) {
    case 'VERY_LIGHT':
      return 'VERY_LIGHT';
    case 'LIGHT':
    case 'BALANCED':
      return 'BALANCED';
    case 'DETAILED':
    case 'EXTENSIVE':
      return 'EXTENSIVE';
    default:
      return FEEDBACK_AMOUNT_DEFAULT;
  }
};

export const normalizeRubricDifficulty = (value) => {
  switch (value) {
    case 'VERY_HARD':
      return 'VERY_HARD';
    case 'HARD':
      return 'VERY_HARD';
    // VERY_EASY is a valid legacy backend value the three-way control no
    // longer renders; it must land on the nearest rendered option (EASY /
    // "Supportive") — falling through to BALANCED would silently make
    // grading stricter than the stored preference.
    case 'VERY_EASY':
      return 'EASY';
    case 'EASY':
    case 'BALANCED':
      return value;
    default:
      return ADVANCED_MARKING_DEFAULTS.rubricDifficulty;
  }
};

// The backend enums carry legacy in-between values (SOME/MANY, VERY_SHORT/
// MEDIUM/LONG, VERY_EASY/HARD) that the three-way/two-way option lists above
// no longer render. Coerce a stored controls object onto the rendered options
// so a segmented control always has an active chip and shows the helper for
// the value that will actually be submitted.
const normalizeSpecificCommentQuantity = (value) => {
  switch (value) {
    case 'FEW':
    case 'BALANCED':
    case 'ALL':
      return value;
    case 'SOME':
      return 'BALANCED';
    case 'MANY':
      return 'ALL';
    default:
      return ADVANCED_MARKING_DEFAULTS.specificCommentQuantity;
  }
};

const normalizeSummaryLength = (value) => {
  switch (value) {
    case 'SHORT':
    case 'FULL':
      return value;
    case 'VERY_SHORT':
      return 'SHORT';
    case 'MEDIUM':
    case 'LONG':
      return 'FULL';
    default:
      return ADVANCED_MARKING_DEFAULTS.summaryLength;
  }
};

const normalizeAiReplyDetail = (value) =>
  AI_REPLY_OPTIONS.some((option) => option.value === value)
    ? value
    : ADVANCED_MARKING_DEFAULTS.aiReplyDetail;

export const normalizeAdvancedMarkingControls = (controls) => {
  const merged = { ...ADVANCED_MARKING_DEFAULTS, ...stripNullValues(controls) };
  return {
    specificCommentQuantity: normalizeSpecificCommentQuantity(
      merged.specificCommentQuantity
    ),
    aiReplyDetail: normalizeAiReplyDetail(merged.aiReplyDetail),
    summaryLength: normalizeSummaryLength(merged.summaryLength),
    sophisticationEnabled: merged.sophisticationEnabled === true,
    rubricDifficulty: normalizeRubricDifficulty(merged.rubricDifficulty),
  };
};

export const getOptionIndex = (options, value, fallbackValue = null) => {
  const resolvedValue = value || fallbackValue;
  const index = options.findIndex((option) => option.value === resolvedValue);
  return index >= 0 ? index : 0;
};

export const getOptionValue = (options, index, fallbackValue) => {
  if (index === null || index === undefined) {
    return fallbackValue || options[0]?.value;
  }
  if (index < 0) {
    return fallbackValue || options[0]?.value;
  }
  if (index >= options.length) {
    return options[options.length - 1]?.value;
  }
  return options[index]?.value;
};

export const getOptionLabel = (options, value, fallbackLabel = '') => {
  const option = options.find((opt) => opt.value === value);
  return option ? option.label : fallbackLabel;
};

// JED-1135: sum the numeric `score` of each selected level. Returns the sum only when EVERY
// criterion whose selected level is matched also carries a numeric score; returns null (→
// legacy band-name heuristic) if any matched selected level lacks a score, or if no criterion
// contributed a scored selection. Mirrors the Java MarkResolver.scoreSumForCriteria exactly so
// FE and backend never diverge on the same data (shared golden fixtures).
const sumSelectedLevelScores = (criterias) => {
  let sum = 0;
  let anyScored = false;
  for (const criteria of criterias) {
    const levels = criteria?.levels || [];
    if (!levels.length) {
      continue;
    }
    const selectedIndex = levels.findIndex(
      (level) => level?.name === criteria?.selectedLevel
    );
    if (selectedIndex < 0) {
      continue;
    }
    const score = levels[selectedIndex]?.score;
    if (typeof score !== 'number' || !Number.isFinite(score)) {
      return null;
    }
    sum += score;
    anyScored = true;
  }
  return anyScored ? sum : null;
};

export const calculateRubricMark = (criterias = [], maxMarks) => {
  const totalMarks = Number(maxMarks);
  if (!Number.isFinite(totalMarks) || totalMarks <= 0) {
    return null;
  }
  if (!Array.isArray(criterias) || criterias.length === 0) {
    return null;
  }

  // JED-1135: prefer exact score summation when every selected level has a numeric score;
  // only fall back to the band-name percentage heuristic for legacy rubrics without scores.
  const scoreSum = sumSelectedLevelScores(criterias);
  if (scoreSum !== null) {
    return Math.max(0, Math.min(totalMarks, Math.round(scoreSum)));
  }

  const percentages = criterias
    .map((criteria) => {
      const levels = criteria?.levels || [];
      if (!levels.length) {
        return null;
      }
      const selectedLevel = criteria?.selectedLevel;
      const selectedIndex = levels.findIndex(
        (level) => level?.name === selectedLevel
      );
      if (selectedIndex < 0) {
        return null;
      }

      return levelPercentages(levels.map((level) => level?.name))[
        selectedIndex
      ];
    })
    .filter((value) => value !== null);

  if (!percentages.length) {
    return null;
  }

  const averagePercentage =
    percentages.reduce((sum, p) => sum + p, 0) / percentages.length;
  const rawMark = (averagePercentage / 100) * totalMarks;
  const rounded = Math.round(rawMark);
  return Math.max(0, Math.min(totalMarks, rounded));
};

export const getBandFromPercentage = (percentage) => {
  if (!Number.isFinite(percentage)) {
    return null;
  }
  if (percentage >= 90) return 'A';
  if (percentage >= 75) return 'B';
  if (percentage >= 60) return 'C';
  if (percentage >= 45) return 'D';
  return 'E';
};

export const formatMarkLabel = (mark, maxMarks) => {
  const numericMark = Number(mark);
  const numericMax = Number(maxMarks);
  if (
    !Number.isFinite(numericMark) ||
    !Number.isFinite(numericMax) ||
    numericMax <= 0
  ) {
    return null;
  }
  const percent = Math.round((numericMark / numericMax) * 100);
  const band = getBandFromPercentage(percent);
  return `${numericMark}/${numericMax} · ${percent}%${
    band ? ` · ${band}` : ''
  }`;
};

// The one advanced-marking object a jeddAIFeedback request carries — exactly
// ONE channel ever goes on the wire:
//   - a student's per-draft choice is {rubricDifficulty, feedbackAmount}: the
//     server maps the amount onto quantity + summary itself and ignores the
//     teacher fields for that caller. Sending both would be redundant at best
//     and, if the server-side channel gate ever regressed, would let stored
//     teacher values ride a student request.
//   - everyone else sends the five stored fields, with the per-draft Advanced
//     tab override spread on top for THIS request only (stripNullValues keeps
//     an explicit `false`, and an untouched popup adds nothing).
// storedSettings must be the ACTIVE, settled settings source (or null when it
// has not settled): disabled queries and keepPreviousData can still hold a
// previous class's values, and a stale rubricDifficulty must never mark this
// draft — undefined fields just make the backend resolve stored settings
// fresh, which is what it does anyway.
export const buildAdvancedMarkingRequestSettings = ({
  storedSettings,
  advancedOverride,
}) => {
  const stored = storedSettings || {};
  if (advancedOverride != null && advancedOverride.feedbackAmount != null) {
    return {
      // Students have always sent their resolved rubricDifficulty; the
      // amount is the only thing this feature adds for them.
      rubricDifficulty: stored.rubricDifficulty || undefined,
      feedbackAmount: advancedOverride.feedbackAmount,
    };
  }
  return {
    rubricDifficulty: stored.rubricDifficulty || undefined,
    specificCommentQuantity: stored.specificCommentQuantity || undefined,
    aiReplyDetail: stored.aiReplyDetail || undefined,
    sophisticationEnabled: stored.sophisticationEnabled || undefined,
    summaryLength: stored.summaryLength || undefined,
    ...stripNullValues(advancedOverride || {}),
  };
};
